import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import { BsSearch, BsX, BsArrowLeft } from "react-icons/bs";
import { AiOutlineShoppingCart } from "react-icons/ai";

import "../../../../assets/css/customer/subscreens/account/MyOrders.css";
import BottomNavigation from "../../../../components/general/bottomnavigation/BottomNavigation";
import MyNavbar from "../../../../components/general/mynavbar/MyNavbar";
import MyContext from "../../../../contexts/AppContext";
import LoadingScreen from "../../../../components/general/animations/Animations.js";

const MyOrders = () => {
  const navigate = useNavigate();
  const { baseUrl, baseUrlForFiles, customerAuthToken,setSelectedOrder } = useContext(MyContext);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const [activeFilter, setActiveFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const ordersPerPage = 5;

  const viewAllOrders = () => {
    const savedCustomer = JSON.parse(localStorage.getItem("loggedInCustomer"));

    const formdata = new FormData();
    formdata.append("chcustomer_id", savedCustomer?.id);

    axios
      .post(`${baseUrl}/customer/order/viewAllOrders`, formdata, 
        {
        headers: { "Content-Type": "multipart/form-data" ,
            "Authorization": `Bearer ${customerAuthToken}`, 
        },
      })
      .then((response) => {
        console.log("Orders Data:", response.data);
        setOrders(response.data.orders);
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  };

  useEffect(() => {
    viewAllOrders();
  }, []);

  const CancelOrder = async (orderId) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "Cancel Order?",
      text: "Are you sure you want to cancel this order?",
      showCancelButton: true,
      confirmButtonColor: "#f44336",
      cancelButtonColor: "#999",
      confirmButtonText: "Yes, cancel it!",
      cancelButtonText: "No, keep it",
    });

    if (!result.isConfirmed) return;

    const formdata = new FormData();
    formdata.append("order_id", orderId);

    axios
      .post(`${baseUrl}/customer/order/cancelOrder`, formdata, {
        headers: { "Content-Type": "multipart/form-data" ,
           "Authorization": `Bearer ${customerAuthToken}`, 
        },
      })
      .then(() => {
        viewAllOrders();
        toast.success("Order cancelled successfully.");
      })
      .catch(() => {
        toast.error("Failed to cancel order. Try again.");
      });
  };

  // ── 24-hour cancel window check ──
  const canCancelOrder = (order) => {
    const created = new Date(order.created_at);
    const now = new Date();
    const hoursSinceOrder = (now - created) / (1000 * 60 * 60);
    return hoursSinceOrder <= 24;
  };

  // ── Filter Orders ──
  const getFilteredOrders = () => {
    let filtered = orders;
    if (activeFilter !== "All") {
      filtered = filtered.filter((o) => o.status === activeFilter);
    }
    if (searchQuery.trim()) {
      filtered = filtered.filter((o) =>
        o.id?.toString().toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }
    return filtered;
  };

  const filteredOrders = getFilteredOrders();
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);
  const currentOrders = filteredOrders.slice(
    (currentPage - 1) * ordersPerPage,
    currentPage * ordersPerPage,
  );

  // ── Status Class ──
  const getStatusClass = (status) => {
    const map = {
      Processing: "co-status-processing",
      completed: "co-status-completed",
      Shipped: "co-status-shipped",
      Cancelled: "co-status-cancelled",
    };
    return map[status] || "";
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      <MyNavbar />

      <div className="co-orders-page">
        <div className="co-orders-container">
          <div className="co-account-shell-container">
            {/* Back Button */}
            <button
              className="co-back-to-account-btn"
              onClick={() => navigate("/account")}
            >
              <BsArrowLeft /> Back to Account
            </button>

            <h1 className="co-orders-title">My Orders</h1>
            <p className="co-orders-subtitle">
              Track and manage your recent orders
            </p>

            {/* ── Filters & Search ── */}
            <div className="co-orders-controls">
              <div className="co-controls-row-equal">
                {["All", "Processing", "completed", "Cancelled"].map(
                  (filter) => (
                    <button
                      key={filter}
                      className={`co-filter-tab-equal ${activeFilter === filter ? "active" : ""}`}
                      onClick={() => {
                        setActiveFilter(filter);
                        setCurrentPage(1);
                      }}
                    >
                      {filter}
                    </button>
                  ),
                )}
                <div
                  className={`co-filter-tab-equal co-search-tab ${searchQuery ? "has-input" : ""}`}
                >
                  <BsSearch />
                  <input
                    type="text"
                    placeholder="Search by Order ID"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                  {searchQuery && (
                    <BsX
                      className="co-clear-search-icon"
                      onClick={() => setSearchQuery("")}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* ── Orders List ── */}
            {currentOrders.length === 0 ? (
              <div className="co-empty-orders">
                <AiOutlineShoppingCart size={80} />
                <h2>No orders found</h2>
                <p>Start shopping to see your orders here!</p>
                <button
                  className="co-start-shopping-btn"
                  onClick={() => navigate("/categories")}
                >
                  Start Shopping
                </button>
              </div>
            ) : (
              <>
                <div className="co-orders-list">
                  {currentOrders.map((order) => (
                    <div key={order.id} className="co-order-card">
                      {/* Order Header */}
                      <div className="co-order-header">
                        <div className="co-order-info">
                          <h3 className="co-order-id">#{order.id}</h3>
                          <p className="co-shop-name">{order.shop_name}</p>
                        </div>
                        <div className="co-order-dates">
                          <div className="co-date-item">
                            <span className="co-date-label">Order Date:</span>
                            <span className="co-date-value">
                              {new Date(order.created_at).toLocaleDateString(
                                "en-PK",
                                {
                                  day: "numeric",
                                  month: "short",
                                  year: "numeric",
                                },
                              )}
                            </span>
                          </div>
                          <div className="co-date-item">
                            <span className="co-date-label">Delivery:</span>
                            <span className="co-date-value delivery-date">
                              {order.delivery_date || "Pending"}
                            </span>
                          </div>
                          <span
                            className={`co-order-status ${getStatusClass(order.status)}`}
                          >
                            {order.status}
                          </span>
                        </div>
                      </div>

                      {/* Order Items */}
                      <div className="co-order-items">
                        <div className="co-order-images">
                          {(order.cart || []).map((item, index) => (
                            <img
                              key={index}
                              src={
                                item.product?.images?.length > 0
                                  ? `${baseUrlForFiles}/${item.product.images[0].image_path}`
                                  : "/placeholder.png"
                              }
                              alt={item.product?.product_name}
                              className="co-order-item-image"
                            />
                          ))}
                        </div>
                        <div className="co-order-description">
                          <p className="co-order-items-text">
                            {(order.cart || [])
                              .slice(0, 2)
                              .map((i) => i.product?.product_name)
                              .join(", ")}
                            {(order.cart || []).length > 2 &&
                              ` +${(order.cart || []).length - 2} more item${(order.cart || []).length > 3 ? "s" : ""}`}
                            {(order.cart || []).some(
                              (i) => i.isCustomized == 1,
                            ) && (
                              <span className="co-customized-badge">
                                Customized
                              </span>
                            )}
                          </p>
                          <p className="co-order-items-count">
                            {(order.cart || []).reduce(
                              (sum, item) => sum + item.quantity,
                              0,
                            )}{" "}
                            items
                          </p>
                        </div>
                      </div>

                      {/* Order Actions */}
                      <div className="co-order-actions">
                        <div className="co-order-total">
                          <span>Total:</span>
                          <span className="co-total-amount">
                            Rs{" "}
                            {Number(order.total_amount || 0).toLocaleString()}
                          </span>
                        </div>
                        <div className="co-action-buttons">
                          {order.status === "Processing" && (
                            <div className="co-cancel-action-col">
                              <button
                                className="co-cancel-order-btn"
                                onClick={() => CancelOrder(order.id)}
                                disabled={!canCancelOrder(order)}
                              >
                                Cancel Order
                              </button>
                              <span className="co-cancel-window-note">
                                {canCancelOrder(order)
                                  ? "Cancel within 24 hours of order"
                                  : "24-hour cancel window has passed"}
                              </span>
                            </div>
                          )}
                          <button
                            className="co-view-details-btn"
                            onClick={() => {
                              setSelectedOrder(order);
                              navigate("/order-details", {
                                state: { orderId: order.id },
                              });
                            }}
                          >
                            View Details
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="co-pagination">
                    <button
                      className="co-page-btn"
                      onClick={() => setCurrentPage((p) => p - 1)}
                      disabled={currentPage === 1}
                    >
                      &lt;
                    </button>
                    {[...Array(totalPages)].map((_, index) => (
                      <button
                        key={index}
                        className={`co-page-btn ${currentPage === index + 1 ? "active" : ""}`}
                        onClick={() => setCurrentPage(index + 1)}
                      >
                        {index + 1}
                      </button>
                    ))}
                    <button
                      className="co-page-btn"
                      onClick={() => setCurrentPage((p) => p + 1)}
                      disabled={currentPage === totalPages}
                    >
                      &gt;
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <BottomNavigation />
      </div>
    </>
  );
};

export default MyOrders;
